import { createSlice, AnyAction, isAnyOf, PayloadAction } from "@reduxjs/toolkit";
import {
  createSlot,
  updateSlot,
  deleteSlot,
  extendSlot,
  refundSlot,
  batchExtend,
  batchRefund,
  fetchSlots,
  fetchSlotsPaginationInfo,
  uploadSlotExcel,
  batchCreateSlots,
  batchUpdateSlots,
  fetchSlotsBatchDetail,
} from "./actions";
import {
  Slot,
  PaginationInfo,
  AppliedFilters,
  BatchSlotSideEffectList,
} from "../Model/Slot";
import { calcRemaining } from "../Domain/UseCase/Utils/DateUtil";
import {
  dtoTriggersRankRecalc,
  expectsAsyncRank,
  isRankCheckInProgress,
} from "../../../Utils/slotRankAsync";

interface SlotState {
  list: Slot[];
  pagination: PaginationInfo | null;
  filters: AppliedFilters | null;
  loading: boolean;
  error?: string;
  defaultPage: {
    slots: Slot[];
    pagination: PaginationInfo | null;
    loaded: boolean;
  };
  needsRefresh: boolean;
  /** 실패 시 행 전체 롤백용 스냅샷 (id → 저장 시점 슬롯) */
  optimisticRollbackById: Record<number, Slot>;
  /** 순위 비동기 계산 대기 중인 슬롯 ID */
  rankCalculatingIds: number[];
}

const initialState: SlotState = { 
  list: [], 
  pagination: null,
  filters: null,
  loading: false,
  defaultPage: {
    slots: [],
    pagination: null,
    loaded: false,
  },
  needsRefresh: false,
  optimisticRollbackById: {},
  rankCalculatingIds: [],
};

function cloneSlot(slot: Slot): Slot {
  if (typeof structuredClone === "function") {
    return structuredClone(slot);
  }
  return JSON.parse(JSON.stringify(slot)) as Slot;
}

function replaceSlotEverywhere(state: SlotState, slotId: number, slot: Slot): void {
  const li = state.list.findIndex((s) => s.slotId === slotId);
  if (li >= 0) state.list[li] = cloneSlot(slot);
  const di = state.defaultPage.slots.findIndex((s) => s.slotId === slotId);
  if (di >= 0) state.defaultPage.slots[di] = cloneSlot(slot);
}

/** 서버가 내려준 필드로 덮되, 목록에만 있던 필드(rank 등)는 유지 */
function upsertMergedServerSlot(state: SlotState, incoming: Slot): void {
  const li = state.list.findIndex((s) => s.slotId === incoming.slotId);
  const di = state.defaultPage.slots.findIndex((s) => s.slotId === incoming.slotId);
  const existing =
    li >= 0 ? state.list[li] : di >= 0 ? state.defaultPage.slots[di] : undefined;
  const merged = (existing ? { ...existing, ...incoming } : incoming) as Slot;
  replaceSlotEverywhere(state, incoming.slotId, merged);
}

function isBatchLegacyIdList(
  payload: BatchSlotSideEffectList,
): payload is number[] {
  return payload.length > 0 && typeof (payload as number[])[0] === "number";
}

function mergePatchIntoSlotMutable(slot: Slot, patch: Partial<Slot>): void {
  Object.assign(slot, patch);
  if (patch.end_date !== undefined || patch.start_date !== undefined) {
    const end =
      patch.end_date !== undefined ? patch.end_date : slot.end_date;
    if (end) slot.remaining_days = calcRemaining(end);
  }
}

function mergePatchEverywhere(state: SlotState, slotId: number, patch: Partial<Slot>): void {
  const li = state.list.findIndex((s) => s.slotId === slotId);
  if (li >= 0) mergePatchIntoSlotMutable(state.list[li], patch);
  const di = state.defaultPage.slots.findIndex((s) => s.slotId === slotId);
  if (di >= 0) mergePatchIntoSlotMutable(state.defaultPage.slots[di], patch);
}

function patchSlotExtendInPlace(target: Slot, days: number): void {
  target.status = "연장";
  target.remaining_days = Math.max(0, (target.remaining_days || 0) + days);
  const currentDate = new Date();
  const endDate = new Date(currentDate);
  endDate.setDate(currentDate.getDate() + target.remaining_days);
  target.end_date = endDate.toISOString().split("T")[0];
}

function patchSlotRefundInPlace(target: Slot): void {
  target.status = "환불";
  target.remaining_days = 0;
  const currentDate = new Date();
  target.end_date = currentDate.toISOString().split("T")[0];
}

function bumpPaginationAfterInsert(pagination: PaginationInfo | null): void {
  if (!pagination) return;
  pagination.total_count += 1;
  const pageSize = pagination.page_size || 20;
  pagination.total_pages = Math.max(1, Math.ceil(pagination.total_count / pageSize));
  pagination.has_next = pagination.current_page < pagination.total_pages;
  pagination.next_page = pagination.has_next ? pagination.current_page + 1 : null;
}

function bumpPaginationAfterRemove(pagination: PaginationInfo | null): void {
  if (!pagination) return;
  pagination.total_count = Math.max(0, pagination.total_count - 1);
  const pageSize = pagination.page_size || 20;
  pagination.total_pages = Math.max(1, Math.ceil(pagination.total_count / pageSize));
  pagination.has_next = pagination.current_page < pagination.total_pages;
  pagination.next_page = pagination.has_next ? pagination.current_page + 1 : null;
}

function insertSlotAtTop(state: SlotState, slot: Slot): void {
  insertSlotsAtTop(state, [slot]);
}

function insertSlotsAtTop(state: SlotState, slots: Slot[]): void {
  if (!slots.length) return;

  const cloned = slots.map(cloneSlot);
  state.list.unshift(...cloned);
  const listPageSize = state.pagination?.page_size;
  if (listPageSize && state.list.length > listPageSize) {
    state.list.length = listPageSize;
  }
  for (let i = 0; i < slots.length; i++) {
    bumpPaginationAfterInsert(state.pagination);
  }

  if (state.defaultPage.loaded) {
    state.defaultPage.slots.unshift(...cloned.map(cloneSlot));
    const defaultPageSize =
      state.defaultPage.pagination?.page_size ?? listPageSize;
    if (defaultPageSize && state.defaultPage.slots.length > defaultPageSize) {
      state.defaultPage.slots.length = defaultPageSize;
    }
    for (let i = 0; i < slots.length; i++) {
      bumpPaginationAfterInsert(state.defaultPage.pagination);
    }
  }
}

function removeSlotById(state: SlotState, id: number): void {
  const hadInList = state.list.some((s) => s.slotId === id);
  const hadInDefault = state.defaultPage.slots.some((s) => s.slotId === id);
  state.list = state.list.filter((s) => s.slotId !== id);
  state.defaultPage.slots = state.defaultPage.slots.filter((s) => s.slotId !== id);
  if (hadInList) bumpPaginationAfterRemove(state.pagination);
  if (hadInDefault) bumpPaginationAfterRemove(state.defaultPage.pagination);
}

function removeOptimisticSlotById(state: SlotState, tempId: number): void {
  const hadInList = state.list.some((s) => s.slotId === tempId);
  const hadInDefault = state.defaultPage.slots.some((s) => s.slotId === tempId);
  state.list = state.list.filter((s) => s.slotId !== tempId);
  state.defaultPage.slots = state.defaultPage.slots.filter(
    (s) => s.slotId !== tempId,
  );
  if (hadInList) bumpPaginationAfterRemove(state.pagination);
  if (hadInDefault) bumpPaginationAfterRemove(state.defaultPage.pagination);
}

function replaceOptimisticSlot(state: SlotState, tempId: number, saved: Slot): void {
  const li = state.list.findIndex((s) => s.slotId === tempId);
  if (li >= 0) {
    state.list[li] = cloneSlot(saved);
  } else {
    insertSlotAtTop(state, saved);
  }

  const di = state.defaultPage.slots.findIndex((s) => s.slotId === tempId);
  if (di >= 0) {
    state.defaultPage.slots[di] = cloneSlot(saved);
  } else if (state.defaultPage.loaded) {
    state.defaultPage.slots.unshift(cloneSlot(saved));
    const defaultPageSize =
      state.defaultPage.pagination?.page_size ?? state.pagination?.page_size;
    if (defaultPageSize && state.defaultPage.slots.length > defaultPageSize) {
      state.defaultPage.slots.pop();
    }
    bumpPaginationAfterInsert(state.defaultPage.pagination);
  }
}

/* ──────────────────────────────── 공통 Matcher ─────────────────────────────── */

const isSlotPending = isAnyOf(
  fetchSlots.pending,
  fetchSlotsPaginationInfo.pending,
  extendSlot.pending,
  refundSlot.pending,
  uploadSlotExcel.pending,
  batchUpdateSlots.pending,
);

const isSlotRejected = isAnyOf(
  fetchSlots.rejected,
  fetchSlotsPaginationInfo.rejected,
  extendSlot.rejected,
  refundSlot.rejected,
  uploadSlotExcel.rejected,
  batchUpdateSlots.rejected,
);

const isDataModifyingAction = isAnyOf(
  uploadSlotExcel.fulfilled,
  batchUpdateSlots.fulfilled,
);

/* ───────────────────────────────────── Slice ───────────────────────────────── */

const slotSlice = createSlice({
  name: "slot",
  initialState,
  reducers: {
    setDefaultPage: (state, action) => {
      state.defaultPage = {
        slots: action.payload.slots,
        pagination: action.payload.pagination,
        loaded: true,
      };
    },
    clearDefaultPage: (state) => {
      state.defaultPage = {
        slots: [],
        pagination: null,
        loaded: false,
      };
    },
    resetRefreshFlag: (state) => {
      state.needsRefresh = false;
    },
    beginOptimisticSlotMerge: (
      state,
      action: PayloadAction<{ id: number; snapshot: Slot; patch: Partial<Slot> }>,
    ) => {
      const { id, snapshot, patch } = action.payload;
      state.optimisticRollbackById[id] = cloneSlot(snapshot);
      mergePatchEverywhere(state, id, patch);
    },
    beginOptimisticBatchExtend: (
      state,
      action: PayloadAction<{
        ids: number[];
        days: number;
        snapshots: Record<number, Slot>;
      }>,
    ) => {
      const { ids, days, snapshots } = action.payload;
      for (const id of ids) {
        const snap = snapshots[id];
        if (snap) state.optimisticRollbackById[id] = cloneSlot(snap);
      }
      for (const id of ids) {
        const li = state.list.findIndex((s) => s.slotId === id);
        if (li >= 0) patchSlotExtendInPlace(state.list[li], days);
        const di = state.defaultPage.slots.findIndex((s) => s.slotId === id);
        if (di >= 0) patchSlotExtendInPlace(state.defaultPage.slots[di], days);
      }
    },
    beginOptimisticBatchRefund: (
      state,
      action: PayloadAction<{ ids: number[]; snapshots: Record<number, Slot> }>,
    ) => {
      const { ids, snapshots } = action.payload;
      for (const id of ids) {
        const snap = snapshots[id];
        if (snap) state.optimisticRollbackById[id] = cloneSlot(snap);
      }
      for (const id of ids) {
        const li = state.list.findIndex((s) => s.slotId === id);
        if (li >= 0) patchSlotRefundInPlace(state.list[li]);
        const di = state.defaultPage.slots.findIndex((s) => s.slotId === id);
        if (di >= 0) patchSlotRefundInPlace(state.defaultPage.slots[di]);
      }
    },
    beginOptimisticCreateSlots: (state, action: PayloadAction<{ slots: Slot[] }>) => {
      insertSlotsAtTop(state, action.payload.slots);
    },
    beginOptimisticDeleteSlots: (
      state,
      action: PayloadAction<{ ids: number[]; snapshots: Record<number, Slot> }>,
    ) => {
      const { ids, snapshots } = action.payload;
      for (const id of ids) {
        const snap = snapshots[id];
        if (snap) state.optimisticRollbackById[id] = cloneSlot(snap);
      }
      for (const id of ids) {
        removeSlotById(state, id);
      }
    },
    finishSlotsRankCalculating: (state, action: PayloadAction<number[]>) => {
      for (const id of action.payload) {
        const apply = (slot: Slot | undefined) => {
          if (!slot) return;
          slot.rank_calculating = false;
        };
        apply(state.list.find((s) => s.slotId === id));
        apply(state.defaultPage.slots.find((s) => s.slotId === id));
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSlots.fulfilled, (state, action) => {
        const calculatingIds = new Set(
          [...state.list, ...state.defaultPage.slots]
            .filter(
              (s) =>
                s.rank_check_status
                  ? isRankCheckInProgress(s.rank_check_status)
                  : s.rank_calculating,
            )
            .map((s) => s.slotId),
        );
        state.list = (Array.isArray(action.payload.slots) ? action.payload.slots : []).map(
          (slot) =>
            slot.rank_check_status
              ? {
                  ...slot,
                  rank_calculating: isRankCheckInProgress(slot.rank_check_status),
                }
              : calculatingIds.has(slot.slotId)
                ? { ...slot, rank_calculating: true }
              : slot,
        );
        state.pagination = action.payload.pagination;
        state.filters = action.payload.filters;
        state.loading = false;
        state.needsRefresh = false;
      })
      .addCase(fetchSlotsPaginationInfo.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(createSlot.fulfilled, (state, action) => {
        const tempId = action.meta.arg?.tempId as number | undefined;
        const dto = action.meta.arg?.dto;
        const calculating =
          action.payload.rank_check_status
            ? isRankCheckInProgress(action.payload.rank_check_status)
            : expectsAsyncRank(action.payload) ||
              (dto ? expectsAsyncRank(dto) : false);
        const saved = {
          ...action.payload,
          rank_calculating: calculating,
          is_optimistic: false,
        } as Slot;

        if (tempId != null && tempId < 0) {
          replaceOptimisticSlot(state, tempId, saved);
        } else {
          insertSlotAtTop(state, saved);
        }

        state.loading = false;
        state.needsRefresh = false;
      })
      .addCase(createSlot.rejected, (state, action: AnyAction) => {
        const tempId = action.meta?.arg?.tempId as number | undefined;
        if (tempId != null && tempId < 0) {
          removeOptimisticSlotById(state, tempId);
        }
        state.loading = false;
        state.error = action.error?.message;
      })
      .addCase(updateSlot.fulfilled, (state, action) => {
        const dto = action.meta.arg.dto as Partial<Slot>;
        const calculating = action.payload.rank_check_status
          ? isRankCheckInProgress(action.payload.rank_check_status)
          : dtoTriggersRankRecalc(dto);
        const saved = {
          ...action.payload,
          rank_calculating: calculating,
        } as Slot;
        replaceSlotEverywhere(state, saved.slotId, saved);
        delete state.optimisticRollbackById[saved.slotId];
        state.loading = false;
        state.needsRefresh = false;
      })
      .addCase(updateSlot.rejected, (state, action: AnyAction) => {
        const id = action.meta?.arg?.id as number | undefined;
        if (id != null && state.optimisticRollbackById[id]) {
          replaceSlotEverywhere(state, id, state.optimisticRollbackById[id]);
          delete state.optimisticRollbackById[id];
        }
        state.loading = false;
        state.error = action.error?.message;
      })
      .addCase(fetchSlotsBatchDetail.fulfilled, (state, action) => {
        for (const slot of action.payload.slots) {
          const existing =
            state.list.find((s) => s.slotId === slot.slotId) ||
            state.defaultPage.slots.find((s) => s.slotId === slot.slotId);
          const merged = {
            ...slot,
            rank_calculating: slot.rank_check_status
              ? isRankCheckInProgress(slot.rank_check_status)
              : existing?.rank_calculating ?? false,
            is_optimistic: false,
          } as Slot;
          replaceSlotEverywhere(state, merged.slotId, merged);
        }
      })
      .addCase(batchUpdateSlots.fulfilled, (state, action) => {
        const patches = action.meta.arg.slots as Partial<Slot>[];
        const shouldCalc = patches.some((p) => dtoTriggersRankRecalc(p));
        if (shouldCalc) {
          for (const id of action.payload.updated_ids ?? []) {
            const apply = (slot: Slot | undefined) => {
              if (!slot) return;
              slot.rank_calculating = true;
              slot.rank_check_status = 'pending';
            };
            apply(state.list.find((s) => s.slotId === id));
            apply(state.defaultPage.slots.find((s) => s.slotId === id));
          }
        }
        state.loading = false;
        state.needsRefresh = true;
      })
      .addCase(deleteSlot.fulfilled, (state, action) => {
        const id = action.meta.arg.id;
        const optimistic = action.meta.arg.optimisticAlreadyApplied === true;

        if (!optimistic) {
          removeSlotById(state, id);
          state.needsRefresh = true;
        } else {
          delete state.optimisticRollbackById[id];
          state.needsRefresh = false;
        }

        state.loading = false;
      })
      .addCase(deleteSlot.rejected, (state, action: AnyAction) => {
        const id = action.meta?.arg?.id as number | undefined;
        if (id != null && state.optimisticRollbackById[id]) {
          insertSlotAtTop(state, state.optimisticRollbackById[id]);
          delete state.optimisticRollbackById[id];
        }
        state.loading = false;
        state.error = action.error?.message;
      })
      .addCase(extendSlot.fulfilled, (state, action) => {
        replaceSlotEverywhere(state, action.payload.slotId, action.payload);
        state.loading = false;
        state.needsRefresh = false;
      })
      .addCase(refundSlot.fulfilled, (state, action) => {
        replaceSlotEverywhere(state, action.payload.slotId, action.payload);
        state.loading = false;
        state.needsRefresh = false;
      })
      .addCase(batchExtend.fulfilled, (state, action) => {
        const payload = action.payload as BatchSlotSideEffectList;
        const { ids: requestedIds, days, optimisticAlreadyApplied } =
          action.meta.arg;
        const optimistic = optimisticAlreadyApplied === true;

        if (!isBatchLegacyIdList(payload)) {
          const slots = payload as Slot[];
          const returnedIds = new Set(slots.map((s) => s.slotId));
          for (const slot of slots) {
            upsertMergedServerSlot(state, slot);
            delete state.optimisticRollbackById[slot.slotId];
          }
          for (const id of requestedIds) {
            if (!returnedIds.has(id) && state.optimisticRollbackById[id]) {
              replaceSlotEverywhere(state, id, state.optimisticRollbackById[id]);
              delete state.optimisticRollbackById[id];
            }
          }
        } else {
          const doneIds = payload as number[];
          const doneSet = new Set(doneIds);
          if (!optimistic) {
            for (const id of doneIds) {
              const li = state.list.findIndex((s) => s.slotId === id);
              if (li >= 0) patchSlotExtendInPlace(state.list[li], days);
              const di = state.defaultPage.slots.findIndex(
                (s) => s.slotId === id,
              );
              if (di >= 0) patchSlotExtendInPlace(state.defaultPage.slots[di], days);
            }
          }
          for (const id of doneIds) delete state.optimisticRollbackById[id];
          for (const id of requestedIds) {
            if (!doneSet.has(id) && state.optimisticRollbackById[id]) {
              replaceSlotEverywhere(state, id, state.optimisticRollbackById[id]);
              delete state.optimisticRollbackById[id];
            }
          }
        }
        state.loading = false;
        state.needsRefresh = false;
      })
      .addCase(batchExtend.rejected, (state, action: AnyAction) => {
        const ids = (action.meta?.arg?.ids ?? []) as number[];
        for (const id of ids) {
          const snap = state.optimisticRollbackById[id];
          if (snap) {
            replaceSlotEverywhere(state, id, snap);
            delete state.optimisticRollbackById[id];
          }
        }
        state.loading = false;
        state.error = action.error?.message;
      })
      .addCase(batchRefund.fulfilled, (state, action) => {
        const payload = action.payload as BatchSlotSideEffectList;
        const { ids: requestedIds, optimisticAlreadyApplied } = action.meta.arg;
        const optimistic = optimisticAlreadyApplied === true;

        if (!isBatchLegacyIdList(payload)) {
          const slots = payload as Slot[];
          const returnedIds = new Set(slots.map((s) => s.slotId));
          for (const slot of slots) {
            upsertMergedServerSlot(state, slot);
            delete state.optimisticRollbackById[slot.slotId];
          }
          for (const id of requestedIds) {
            if (!returnedIds.has(id) && state.optimisticRollbackById[id]) {
              replaceSlotEverywhere(state, id, state.optimisticRollbackById[id]);
              delete state.optimisticRollbackById[id];
            }
          }
        } else {
          const doneIds = payload as number[];
          const doneSet = new Set(doneIds);
          if (!optimistic) {
            for (const id of doneIds) {
              const li = state.list.findIndex((s) => s.slotId === id);
              if (li >= 0) patchSlotRefundInPlace(state.list[li]);
              const di = state.defaultPage.slots.findIndex(
                (s) => s.slotId === id,
              );
              if (di >= 0) patchSlotRefundInPlace(state.defaultPage.slots[di]);
            }
          }
          for (const id of doneIds) delete state.optimisticRollbackById[id];
          for (const id of requestedIds) {
            if (!doneSet.has(id) && state.optimisticRollbackById[id]) {
              replaceSlotEverywhere(state, id, state.optimisticRollbackById[id]);
              delete state.optimisticRollbackById[id];
            }
          }
        }
        state.loading = false;
        state.needsRefresh = false;
      })
      .addCase(batchRefund.rejected, (state, action: AnyAction) => {
        const ids = (action.meta?.arg?.ids ?? []) as number[];
        for (const id of ids) {
          const snap = state.optimisticRollbackById[id];
          if (snap) {
            replaceSlotEverywhere(state, id, snap);
            delete state.optimisticRollbackById[id];
          }
        }
        state.loading = false;
        state.error = action.error?.message;
      })
      .addCase(uploadSlotExcel.fulfilled, (state) => {
        state.loading = false;
        state.needsRefresh = true;
      })
      .addCase(batchCreateSlots.fulfilled, (state, action) => {
        const tempIds = action.meta.arg?.tempIds as number[] | undefined;
        const optimistic = action.meta.arg?.optimisticAlreadyApplied === true;
        const requestSlots = action.meta.arg?.request?.slots ?? [];
        const saved = (Array.isArray(action.payload) ? action.payload : []).map(
          (slot, index) =>
            ({
              ...slot,
              rank_calculating:
                slot.rank_check_status
                  ? isRankCheckInProgress(slot.rank_check_status)
                  : expectsAsyncRank(slot) ||
                    expectsAsyncRank(requestSlots[index] ?? requestSlots[0] ?? {}),
              is_optimistic: false,
            }) as Slot,
        );

        if (optimistic && tempIds?.length) {
          for (let i = 0; i < tempIds.length; i++) {
            const tempId = tempIds[i];
            const serverSlot = saved[i];
            if (serverSlot) {
              replaceOptimisticSlot(state, tempId, serverSlot);
            } else {
              removeOptimisticSlotById(state, tempId);
            }
          }
          for (let i = tempIds.length; i < saved.length; i++) {
            insertSlotAtTop(state, saved[i]);
          }
        } else if (saved.length) {
          insertSlotsAtTop(state, saved);
          state.needsRefresh = true;
        }

        state.loading = false;
        if (optimistic) state.needsRefresh = false;
      })
      .addCase(batchCreateSlots.rejected, (state, action: AnyAction) => {
        const tempIds = (action.meta?.arg?.tempIds ?? []) as number[];
        for (const tempId of tempIds) {
          removeOptimisticSlotById(state, tempId);
        }
        state.loading = false;
        state.error = action.error?.message;
      })

      .addMatcher(isSlotPending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addMatcher(isSlotRejected, (state, action: AnyAction) => {
        state.loading = false;
        state.error = action.error?.message;
      })
      .addMatcher(isDataModifyingAction, (state) => {
        state.needsRefresh = true;
      });
  },
});

export const {
  setDefaultPage,
  clearDefaultPage,
  resetRefreshFlag,
  beginOptimisticSlotMerge,
  beginOptimisticBatchExtend,
  beginOptimisticBatchRefund,
  beginOptimisticCreateSlots,
  beginOptimisticDeleteSlots,
  finishSlotsRankCalculating,
} = slotSlice.actions;
export default slotSlice.reducer;
